import { Locale, Translations } from "./types";

type EmbeddedTranslations = {
  total: string;
  fees: (customerFee: string) => string;
  offeredFees: string;
  payButton: (amount: string) => string;
  noPlanError: string;
  deferredCaptureExplanation: string;
  deferredFirstInstallment: string;
  deferredMonth: (index: number) => string;
};

type AllTranslations = {
  modal: Translations;
  embedded: EmbeddedTranslations;
};

// Copied from checkout's Crowdin translations (src/intl/messages)
const translations: Record<Lowercase<Locale>, AllTranslations> = {
  fr: {
    modal: {
      closeModalMessage: "Voulez-vous vraiment quitter la page de paiement ?",
      closeModalButtonHover: "Abandonner le paiement avec Alma",
    },
    embedded: {
      total: "Total",
      fees: (fee) => `Dont ${fee} de frais à payer aujourd'hui.`,
      offeredFees: "Les frais de paiement sont offerts",
      payButton: (amount) => `Payer ${amount}`,
      noPlanError:
        "Une erreur est survenue, aucun échéancier n'est disponible, merci de réessayer.",
      deferredCaptureExplanation:
        "Vos prélèvements commenceront lorsque nous aurons validé votre commande.",
      deferredFirstInstallment: "Au traitement de la commande",
      deferredMonth: (index) => `${index} mois après`,
    },
  },
  en: {
    modal: {
      closeModalMessage: "Do you really want to leave the checkout page?",
      closeModalButtonHover: "Cancel the payment with Alma",
    },
    embedded: {
      total: "Total",
      fees: (fee) => `Including ${fee} of fees to be paid today.`,
      offeredFees: "Payment fees are free",
      payButton: (amount) => `Pay ${amount}`,
      noPlanError:
        "An error has occurred, no payment schedule is available, please try again.",
      deferredCaptureExplanation:
        "Your direct debits will start as soon as we have validated your order.",
      deferredFirstInstallment: "When processing the order",
      deferredMonth: (index) => `${index} month after`,
    },
  },
  de: {
    modal: {
      closeModalMessage: "Möchten Sie die Zahlungsseite wirklich verlassen?",
      closeModalButtonHover: "Zahlung mit Alma abbrechen",
    },
    embedded: {
      total: "Gesamtbetrag",
      fees: (fee) => `Davon ${fee} Gebühren, die heute bezahlt werden müssen.`,
      offeredFees: "Die Zahlungsgebühren sind erlassen",
      payButton: (amount) => `${amount} zahlen`,
      noPlanError:
        "Es ist ein Fehler aufgetreten, es ist kein Zahlungsplan verfügbar. Bitte versuchen Sie es erneut.",
      deferredCaptureExplanation:
        "Ihre Abbuchungen beginnen, sobald wir Ihre Bestellung freigegeben haben.",
      deferredFirstInstallment: "Bei der Bearbeitung der Bestellung",
      deferredMonth: (index) =>
        index === 1 ? `${index} Monat später` : `${index} Monate später`,
    },
  },
  it: {
    modal: {
      closeModalMessage: "Vuoi davvero lasciare la pagina di pagamento?",
      closeModalButtonHover: "Annullare il pagamento con Alma",
    },
    embedded: {
      total: "Totale",
      fees: (fee) =>
        `Inclusa la commissione di ${fee} per il pagamento rateale.`,
      offeredFees: "Nessuna commissione di pagamento",
      payButton: (amount) => `Paga ${amount}`,
      noPlanError:
        "Si è verificato un errore, non è disponibile nessun piano di pagamento, riprovare.",
      deferredCaptureExplanation:
        "Gli addebiti diretti inizieranno non appena avremo convalidato l'ordine.",
      deferredFirstInstallment: "Elaborazione dell'ordine",
      deferredMonth: (index) =>
        index === 1 ? `${index} mese dopo` : `${index} mesi dopo`,
    },
  },
  es: {
    modal: {
      closeModalMessage: "¿De verdad quieres salir de la página de pago?",
      closeModalButtonHover: "Cancelar el pago con Alma",
    },
    embedded: {
      total: "Total",
      fees: (fee) => `De los cuales ${fee} son gastos administrativos.`,
      offeredFees: "Las comisiones de pago son gratuitas",
      payButton: (amount) => `Pagar ${amount}`,
      noPlanError: "Se ha producido un error, inténtelo de nuevo.",
      deferredCaptureExplanation:
        "La domiciliación bancaria se iniciará en cuanto hayamos validado tu pedido.",
      deferredFirstInstallment: "Al procesarse el pedido",
      deferredMonth: (index) =>
        index === 1 ? `${index} mes después` : `${index} meses después`,
    },
  },
  nl: {
    modal: {
      closeModalMessage: "Weet je zeker dat je de betaalpagina wilt verlaten?",
      closeModalButtonHover: "Annuleer betaling met Alma",
    },
    embedded: {
      total: "Totaal",
      fees: (fee) => `Waarvan ${fee} kosten die vandaag moeten worden betaald.`,
      offeredFees: "Geen extra kosten",
      payButton: (amount) => `Betaal ${amount}`,
      noPlanError:
        "Er is een fout opgetreden, er is geen betaalschema beschikbaar, probeer het opnieuw.",
      deferredCaptureExplanation:
        "Uw automatische incasso's beginnen zodra we uw bestelling hebben gevalideerd.",
      deferredFirstInstallment: "Op het moment dat de bestelling wordt verwerkt",
      deferredMonth: (index) => `${index} maand na`,
    },
  },
  pt: {
    modal: {
      closeModalMessage: "Quer mesmo sair da página de pagamento?",
      closeModalButtonHover: "Cancelar o pagamento com a Alma",
    },
    embedded: {
      total: "Total",
      fees: (fee) => `Do qual ${fee} deve ser pago hoje.`,
      offeredFees: "As despesas de pagamento são gratuitas",
      payButton: (amount) => `Pagar ${amount}`,
      noPlanError:
        "Ocorreu um erro, não existe nenhum plano de pagamento disponível, tente novamente.",
      deferredCaptureExplanation:
        "Os seus débitos diretos começarão assim que tivermos validado a sua encomenda.",
      deferredFirstInstallment: "Processamento de encomendas",
      deferredMonth: (index) =>
        index === 1 ? `${index} mês seguinte` : `${index} meses seguintes`,
    },
  },
};

function isSupported(language: string): language is Lowercase<Locale> {
  return Object.keys(translations).includes(language);
}

// Same resolution as the former checkout iframe, which received `locale ?? "FR"`
export function getLanguage(locale?: string | null): Lowercase<Locale> {
  const language = String(locale ?? "FR").toLowerCase();
  if (isSupported(language)) return language;

  const browserLanguage = navigator.language.split("-")[0];
  return isSupported(browserLanguage) ? browserLanguage : "en";
}

export function getTranslations(locale?: string | null) {
  return translations[getLanguage(locale)];
}
